import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import PatientOnboarding from './PatientOnboarding';
import PatientSurveys from './PatientSurveys';
import PatientEducation from './PatientEducation';
import PatientProfileSection from './PatientProfileSection';
import { GrHomeRounded, GrCompliance, GrCatalog, GrChat, GrUser } from 'react-icons/gr';
import '../styles/PatientDashboard.css';

const PatientDashboard = () => {
  const { currentUser, logout } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Set active tab based on current route
  const getActiveTab = () => {
    const { pathname } = window.location;
    if (pathname.includes('/profile')) return 'profile';
    if (pathname.includes('/messages')) return 'messages';
    if (pathname.includes('/education')) return 'education';
    if (pathname.includes('/surveys')) return 'surveys';
    return 'overview';
  };

  const activeTab = getActiveTab();

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        if (currentUser) {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.role === 'patient' && !userData.onboardingComplete) {
              setShowOnboarding(true);
            }
          }
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [currentUser]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    window.location.reload();
  };

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  if (showOnboarding) {
    return <PatientOnboarding user={currentUser} onComplete={handleOnboardingComplete} />;
  }

  const OverviewContent = () => (
    <div className="welcome-section">
      <div className="welcome-card">
        <div className="patient-avatar">
          {currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase()}
        </div>
        <h2>Welcome to your Health Portal</h2>
        <p>Manage your health information and connect with your healthcare providers.</p>
      </div>

      <div className="info-grid">
        <div className="info-card">
          <h3>Personal Information</h3>
          <div className="info-item">
            <label>Name:</label>
            <span>{currentUser.name}</span>
          </div>
          {currentUser.email && (
            <div className="info-item">
              <label>Email:</label>
              <span>{currentUser.email}</span>
            </div>
          )}
          {currentUser.phone && (
            <div className="info-item">
              <label>Phone:</label>
              <span>{currentUser.phone}</span>
            </div>
          )}
          {currentUser.dateOfBirth && (
            <div className="info-item">
              <label>Date of Birth:</label>
              <span>{new Date(currentUser.dateOfBirth).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        <div className="info-card">
          <h3>Medical Information</h3>
          {currentUser.medicalHistory ? (
            <div className="medical-history">
              <p>{currentUser.medicalHistory}</p>
            </div>
          ) : (
            <p className="no-data">No medical history available</p>
          )}
        </div>

        <div className="info-card">
          <h3>Recent Activity</h3>
          <div className="activity-item">
            <div className="activity-icon">📋</div>
            <div className="activity-content">
              <div className="activity-title">Account Created</div>
              <div className="activity-date">
                {new Date(currentUser.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Logo" className="header-logo" />
          <div className="user-info">
            <span>Welcome, {currentUser.name}</span>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-content">
          <Routes>
            <Route path="/" element={<OverviewContent />} />
            <Route path="/surveys/*" element={<PatientSurveys />} />
            <Route path="/education/*" element={<PatientEducation />} />
            <Route path="/messages/*" element={
              <div className="messages-tab">
                <div className="coming-soon">
                  <h2>Messages</h2>
                  <p>Coming soon! You'll be able to securely message your healthcare team here.</p>
                </div>
              </div>
            } />
            <Route path="/profile" element={<PatientProfileSection />} />
          </Routes>
        </div>
      </main>

      <nav className="bottom-nav">
        <button 
          className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => navigate('/patient')}
        >
          <GrHomeRounded className="nav-icon" />
          <span className="nav-label">Home</span>
        </button>
        <button 
          className={`nav-item ${activeTab === 'surveys' ? 'active' : ''}`}
          onClick={() => navigate('/patient/surveys')}
        >
          <GrCompliance className="nav-icon" />
          <span className="nav-label">Surveys</span>
        </button>
        <button 
          className={`nav-item ${activeTab === 'education' ? 'active' : ''}`}
          onClick={() => navigate('/patient/education')}
        >
          <GrCatalog className="nav-icon" />
          <span className="nav-label">Education</span>
        </button>
        <button 
          className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => navigate('/patient/messages')}
        >
          <GrChat className="nav-icon" />
          <span className="nav-label">Messages</span>
        </button>
        <button 
          className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => navigate('/patient/profile')}
        >
          <GrUser className="nav-icon" />
          <span className="nav-label">Profile</span>
        </button>
      </nav>
    </div>
  );
};

export default PatientDashboard;
